import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { WorkFlowDataModel } from "@/models/api/administration/workflow/WorkFlowDataModel";
import { WorkFlowModel } from "@/models/api/administration/workflow/WorkFlowModel";
import { WorkFlowItemModel } from "@/models/api/administration/workflow/WorkFlowItemModel";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  getWorkFlowList,
  getWorkFlowDataList,
  getWorkFlowItemList,
  postEditWorkFlow,
  postEditWorkFlowItem,
  postCreateWorkFlowFromPrototype,
} from "@/requests/workflow";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { WorkFlowEdit } from "@/models/api/administration/workflow/WorkFlowEdit";

export interface AdministrationWorkFlowStateInterface {
  isLoading: boolean;
  isEditDialog: boolean;
  isEditItemDialog: boolean;
  workFlowListCount: number | null;
  workFlowList: Array<WorkFlowModel>;
  workFlowDataList: Array<WorkFlowDataModel>;
  workFlowItemList: Array<WorkFlowItemModel>;
  workFlowFilters: Array<CriterionItemRequest> | null;
}

export const AdministrationWorkFlowState: AdministrationWorkFlowStateInterface = {
  isLoading: false,
  isEditDialog: false,
  isEditItemDialog: false,
  workFlowList: new Array<WorkFlowModel>(),
  workFlowListCount: null,
  workFlowDataList: new Array<WorkFlowDataModel>(),
  workFlowItemList: new Array<WorkFlowItemModel>(),
  workFlowFilters: null,
};

const AdministrationWorkFlowModule = defineModule({
  state: AdministrationWorkFlowState as AdministrationWorkFlowStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setIsEditDialog(state, param: boolean) {
      state.isEditDialog = param;
    },
    setIsEditItemDialog(state, param: boolean) {
      state.isEditItemDialog = param;
    },
    setWorkFlowListCount(state, param: number) {
      state.workFlowListCount = param;
    },
    setWorkFlowList(state, param: Array<WorkFlowModel>) {
      state.workFlowList = param;
    },
    setWorkFlowDataList(state, param: Array<WorkFlowDataModel>) {
      state.workFlowDataList = param;
    },
    setWorkFlowItemList(state, param: Array<WorkFlowItemModel>) {
      state.workFlowItemList = param;
    },
    setWorkFlowFilters(state, param: Array<CriterionItemRequest> | null) {
      state.workFlowFilters = param;
    },
  },
  actions: {
    async downloadWorkFlowList(
      context,
      param: {
        count: number;
        page: number;
      }
    ) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getWorkFlowList({
        count: param.count,
        page: param.page,
        criterions: state.workFlowFilters,
        filterName: FILTER_NAMES.WorkFlowListFilter,
      });
      commit.setWorkFlowList(response.items);
      commit.setWorkFlowListCount(response.totalCount);
      commit.setIsLoading(false);
    },
    async downloadWorkFlowDataList(
      context,
      param: {
        workFlowId: number;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getWorkFlowDataList(param.workFlowId);
      commit.setWorkFlowDataList(response);
      commit.setIsLoading(false);
    },
    async downloadWorkFlowItemList(
      context,
      param: {
        workFlowId: number;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getWorkFlowItemList(param.workFlowId);
      commit.setWorkFlowItemList(response);
      commit.setIsLoading(false);
    },
    async updateWorkFlow(
      context,
      param: {
        workFlowId: number;
        workFlowEdit: WorkFlowEdit;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await postEditWorkFlow(param.workFlowId, param.workFlowEdit);
      commit.setIsLoading(false);
    },
    async updateWorkFlowItem(
      context,
      param: {
        workFlowItemId: number;
        state: number;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await postEditWorkFlowItem(param.workFlowItemId, param.state);
      commit.setIsLoading(false);
    },
    async createWorkFlowFromPrototype(
      context,
      param: {
        prototypeName: string;
        bindingId: string;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await postCreateWorkFlowFromPrototype(param.prototypeName, param.bindingId);
      commit.setIsLoading(false);
    },
  },
});

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
export default AdministrationWorkFlowModule;
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationWorkFlowModule);
